import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetRejectedCancelledBetsQuery } from '../services/sports/sportsApi';
import Loading from '../components/Loading';

const BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const FilterIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
  </svg>
);

const EmptyState = () => (
  <tr>
    <td colSpan="10" className="py-16 text-center">
      <div className="flex flex-col items-center gap-3 text-slate-400">
        <svg className="w-12 h-12 text-slate-300" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
          <g transform="translate(0 1)" fill="none" fillRule="evenodd">
            <ellipse fill="#e2e8f0" cx="32" cy="33" rx="32" ry="7"/>
            <g fill="#cbd5e1" fillRule="nonzero">
              <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"/>
              <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"/>
            </g>
          </g>
        </svg>
        <p className="text-sm font-medium">No bets found</p>
        <p className="text-xs text-slate-400">Try changing the filter</p>
      </div>
    </td>
  </tr>
);

const RejectedBetsPage = () => {
  const navigate = useNavigate();
  const { matchId, matchName } = useParams();

  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedBetType] = useState('fancy');

  const { data: betsData, isLoading, error } = useGetRejectedCancelledBetsQuery({
    eventId: matchId,
    betType: selectedBetType,
    status: selectedFilter,
  });

  const fancyBets = betsData?.fancy || [];
  const bookmakerBets = betsData?.bookmaker || [];
  const allBets = [...fancyBets, ...bookmakerBets];

  return (
    <div className="w-full animate-fadeIn">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">

        {/* ── Page Header ────────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-[#0c1524] to-[#162032] px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-orange-400 rounded-full" />
            <h1 className="text-white font-semibold text-base tracking-wide">Rejected & Cancelled Bets</h1>
            {matchName && (
              <span className="text-orange-300/70 text-xs font-medium hidden sm:block">
                — {decodeURIComponent(matchName)}
              </span>
            )}
          </div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-all"
          >
            <BackIcon />
            Back
          </button>
        </div>

        {/* ── Filters ────────────────────────────────────────────────── */}
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <span className="text-slate-400">
              <FilterIcon />
            </span>
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="w-44 px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition-all"
            >
              <option value="all">All Status</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* ── Table ──────────────────────────────────────────────────── */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-16">
              <Loading />
            </div>
          ) : error ? (
            <div className="py-10 text-center">
              <p className="text-red-500 text-sm">{error.message || 'Failed to load bets'}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0F2327] text-white">
                  {['Rate/Size','Amount','Type','Team','Client','Agent','Date','Loss','Profit','Status'].map(h => (
                    <th key={h} className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap border-b border-gray-600">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allBets.length === 0 ? (
                  <EmptyState />
                ) : (
                  allBets.map((bet, index) => {
                    const betType = bet.type?.toLowerCase();
                    const isYesBackLagai = betType === 'yes' || betType === 'back' || betType === 'lagai';
                    const rowBg = isYesBackLagai ? '#eff8ff' : '#fff1f5';

                    const formattedDate = bet.createdAt
                      ? new Date(bet.createdAt).toLocaleString('en-GB', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit', second: '2-digit',
                          hour12: true,
                        })
                      : '-';

                    const isRejected = bet.status?.toLowerCase() === 'rejected';

                    return (
                      <tr
                        key={bet.id || index}
                        style={{ backgroundColor: rowBg }}
                        className="border-b border-slate-100 hover:brightness-95 transition-all"
                      >
                        <td className="px-3 py-2.5 text-slate-700">
                          {bet.rate && bet.size ? `${bet.rate}/${bet.size}` : (bet.rate || '-')}
                        </td>
                        <td className="px-3 py-2.5 font-medium text-slate-800">{bet.amount || '-'}</td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold
                            ${isYesBackLagai ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                            {bet.type || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-700">{bet.fancyName || bet.team || '-'}</td>
                        <td className="px-3 py-2.5 text-slate-700">{bet.client?.username || '-'}</td>
                        <td className="px-3 py-2.5 text-slate-600">{bet.agent?.username || '-'}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">{formattedDate}</td>
                        <td className="px-3 py-2.5 font-medium text-red-600">{bet.loss || '-'}</td>
                        <td className="px-3 py-2.5 font-medium text-emerald-600">{bet.profit || '-'}</td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold
                            ${isRejected
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-700'
                            }`}>
                            {bet.bet_status || bet.status?.toUpperCase() || '-'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  );
};

export default RejectedBetsPage;
